import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, ArrowLeft } from 'lucide-react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

// Advanced AI Knowledge Base
const knowledgeBase = {
  greetings: [
    'Halo! Ada yang bisa saya bantu?',
    'Hi! Senang bertemu dengan Anda. Ada pertanyaan?',
    'Hai! Saya siap membantu Anda hari ini.',
  ],
  about: {
    mahin: 'Mahin Utsman Nawawi adalah seorang profesional yang memiliki latar belakang hukum (S.H, C.D.M.P) dan telah beralih ke dunia teknologi. Beliau spesialisasi dalam No-Code Development, AI Prompt Engineering, dan Digital Marketing dengan motto "Law & Tech in Harmony – Dari Pasal ke Pixel".',
    skills: 'Mahin ahli dalam berbagai teknologi seperti React, Next.js, Supabase, serta platform no-code seperti Bubble.io dan Webflow. Beliau juga berpengalaman dalam AI tools seperti ChatGPT, Claude, dan Midjourney.',
    contact: 'Anda bisa menghubungi Mahin melalui WhatsApp di +62 813 2044 2174 atau email ke nawawnawawimahinutsman@gmail.com',
  },
  technology: {
    react: 'React adalah library JavaScript untuk membangun user interface. Dikembangkan oleh Facebook, React memungkinkan developer membuat komponen UI yang reusable dan efficient.',
    ai: 'Artificial Intelligence (AI) adalah simulasi kecerdasan manusia dalam mesin. AI dapat digunakan untuk berbagai aplikasi seperti chatbot, analisis data, computer vision, dan banyak lagi.',
    nocode: 'No-code development memungkinkan siapa saja membuat aplikasi tanpa menulis code. Platform seperti Bubble, Webflow, dan Zapier membuat pengembangan aplikasi lebih accessible.',
    web3: 'Web3 adalah generasi baru internet yang terdesentralisasi menggunakan teknologi blockchain. Web3 memberikan kontrol lebih kepada pengguna atas data dan aset digital mereka.',
    blockchain: 'Blockchain adalah teknologi distributed ledger yang mencatat transaksi secara aman dan transparan. Digunakan dalam cryptocurrency, smart contracts, dan aplikasi terdesentralisasi.',
  },
  help: 'Tentu! Saya bisa membantu Anda dengan:\n\n- Informasi tentang Mahin Utsman Nawawi\n- Pertanyaan seputar teknologi (React, AI, No-Code, Web3, dll)\n- Saran tentang pengembangan aplikasi\n- Rekomendasi tools dan resources\n- Informasi kontak\n\nSilakan tanyakan apa saja!',
  projects: 'Mahin memiliki berbagai projek yang menunjukkan keahliannya:\n\n1. Mini E-Commerce - Platform belanja online dengan React & Supabase\n2. Todo App - Aplikasi CRUD dengan real-time database\n3. AI Chatbot - Asisten virtual interaktif\n4. Contract Generator - Generator kontrak hukum dengan export PDF\n5. Web3 Wallet - Aplikasi wallet cryptocurrency\n\nSemua projek ini bisa Anda lihat di bagian Projects!',
};

export default function ChatbotDemo() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Halo! Saya adalah AI Chatbot Assistant yang dibangun untuk membantu Anda. Saya memiliki pengetahuan tentang teknologi, projek Mahin Utsman Nawawi, dan berbagai topik menarik lainnya.\n\nKetik "help" untuk melihat apa yang bisa saya bantu!',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [conversationContext, setConversationContext] = useState<{
    topics: string[];
    previousQuestions: string[];
    lastTopic: string | null;
    followUpCount: number;
  }>({
    topics: [],
    previousQuestions: [],
    lastTopic: null,
    followUpCount: 0,
  });

  const getIntelligentResponse = (userMessage: string, context: typeof conversationContext): string => {
    const msg = userMessage.toLowerCase().trim();
    const { previousQuestions = [], lastTopic = null, followUpCount = 0 } = context;
    
    // Greetings
    if (/^(hello|hi|hai|halo|helo|hey|assalamualaikum)$/i.test(msg)) {
      return knowledgeBase.greetings[Math.floor(Math.random() * knowledgeBase.greetings.length)];
    }
    
    // Help & Capabilities
    if (msg.includes('help') || msg.includes('bantuan') || msg.includes('bisa apa')) {
      return knowledgeBase.help;
    }
    
    // About Mahin
    if (msg.includes('mahin') || msg.includes('siapa') && msg.includes('pemilik')) {
      return knowledgeBase.about.mahin;
    }
    
    if (msg.includes('skill') || msg.includes('keahlian') || msg.includes('kemampuan')) {
      return knowledgeBase.about.skills;
    }
    
    if (msg.includes('kontak') || msg.includes('hubungi') || msg.includes('email') || msg.includes('whatsapp')) {
      return knowledgeBase.about.contact;
    }
    
    // Projects
    if (msg.includes('project') || msg.includes('projek') || msg.includes('portofolio') || msg.includes('portfolio')) {
      return knowledgeBase.projects;
    }
    
    // Technology Topics with enhanced context awareness
    if (msg.includes('react')) {
      // Check if this is a follow-up to a previous React question
      if (lastTopic === 'technology' && followUpCount > 1 && (
        msg.includes('jelaskan') || msg.includes('detail') || msg.includes('lagi') ||
        msg.includes('apa itu') || msg.includes('bagaimana')
      )) {
        return 'React memiliki beberapa konsep kunci:\n\n🔹 **Components** -Building blocks yang reusable\n🔹 **JSX** - Syntax untuk menulis HTML dalam JavaScript\n🔹 **Virtual DOM** - Sistem optimasi performa\n🔹 **State & Props** - Manajemen data aplikasi\n🔹 **Hooks** - Modern way untuk functional components\n\nAspek mana yang ingin Anda pelajari lebih detail?';
      }
      return knowledgeBase.technology.react;
    }
    
    if (msg.includes('ai') || msg.includes('artificial intelligence') || msg.includes('machine learning')) {
      return knowledgeBase.technology.ai;
    }
    
    if (msg.includes('no-code') || msg.includes('nocode') || msg.includes('bubble') || msg.includes('webflow')) {
      return knowledgeBase.technology.nocode;
    }
    
    if (msg.includes('web3') || msg.includes('web 3')) {
      return knowledgeBase.technology.web3;
    }
    
    if (msg.includes('blockchain') || msg.includes('crypto') || msg.includes('cryptocurrency')) {
      return knowledgeBase.technology.blockchain;
    }
    
    // General Tech
    if (msg.includes('teknologi') || msg.includes('tech')) {
      return 'Teknologi berkembang sangat pesat! Saya bisa membantu Anda memahami berbagai topik seperti React, AI, No-Code Development, Web3, dan Blockchain. Mana yang ingin Anda pelajari lebih dalam?';
    }
    
    // Questions about capabilities
    if (msg.includes('kamu') || msg.includes('anda') || msg.includes('chatbot')) {
      return 'Saya adalah AI Chatbot Assistant yang dibangun dengan algoritma NLP untuk memahami pertanyaan Anda. Saya bisa memberikan informasi tentang teknologi, projek portfolio, dan menjawab berbagai pertanyaan umum. Saya terus belajar untuk memberikan respons yang lebih baik!';
    }
    
    // Gratitude
    if (msg.includes('terima kasih') || msg.includes('thanks') || msg.includes('thank you')) {
      return 'Sama-sama! Senang bisa membantu Anda. Ada lagi yang ingin ditanyakan?';
    }
    
    // Goodbye
    if (msg.includes('bye') || msg.includes('selamat tinggal') || msg.includes('sampai jumpa')) {
      return 'Sampai jumpa! Terima kasih sudah menggunakan AI Chatbot Assistant. Semoga harimu menyenangkan!';
    }
    
    // Context-aware clarification questions
    if ((msg.includes('jelaskan') || msg.includes('detail') || msg.includes('lagi') || msg.includes('selain') || msg.includes('yang lain')) && lastTopic) {
      if (lastTopic === 'technology') {
        const techKeywords = ['react', 'ai', 'web3', 'blockchain', 'nocode', 'teknologi', 'tech'];
        const mentionedTech = techKeywords.find(keyword => msg.includes(keyword));
        
        if (mentionedTech) {
          // User is asking about specific technology
          return `Baik! Mari saya jelaskan tentang ${mentionedTech.toUpperCase()} secara detail. ${mentionedTech === 'react' ? 'React adalah library JavaScript yang sangat powerful untuk membangun user interface yang interaktif dan responsive. React menggunakan konsep Virtual DOM untuk optimasi performa, dan memiliki ekosistem yang besar dengan banyak tools dan library pendukung.' : 'Apa aspek khusus dari ' + mentionedTech + ' yang ingin Anda ketahui?'}`;
        } else {
          // User asking for more technology topics
          return 'Berikut teknologi lainnya yang bisa saya jelaskan:\n\n🔹 **Vue.js** - Progressive JavaScript framework\n🔹 **Angular** - Enterprise-grade web framework\n🔹 **Node.js** - JavaScript runtime untuk server-side\n🔹 **Python** - Programming language untuk AI & Data Science\n🔹 **TypeScript** - Superset of JavaScript dengan type safety\n\nTeknologi mana yang ingin Anda pelajari?';
        }
      }
      
      if (lastTopic === 'about') {
        return 'Tentang Mahin, saya bisa jelaskan lebih detail tentang:\n\n👨‍💻 **Background** - Dari hukum ke teknologi\n🎯 **Spesialisasi** - No-code, AI, Web Development\n💼 **Pengalaman** - Client projects dan portfolio\n📚 **Pendidikan** - S.H, C.D.M.P certification\n🔗 **Network** - Professional connections\n\nAspek mana yang ingin diketahui lebih lanjut?';
      }
      
      // Context-aware acknowledgment
      if (msg.includes('terima kasih') || msg.includes('thanks')) {
        return `Senang bisa membantu Anda! ${followUpCount > 1 ? 'Saya lihat Anda sangat tertarik dengan topik ini.' : 'Ada pertanyaan lain yang bisa saya bantu?'}`;
      }
    }
    
    // Smart follow-up detection
    if (previousQuestions.length > 0 && (msg.includes('ya') || msg.includes('tentu') || msg.includes('silakan'))) {
      return 'Bagus! Saya siap membantu Anda dengan pertanyaan tersebut. Apa yang spesifik ingin Anda ketahui?';
    }
    
    // Topic transition handling
    if (previousQuestions.length > 1) {
      const recentTopics = previousQuestions.slice(-3);
      if (recentTopics.some(q => q.includes('react')) && msg.includes('ai')) {
        return 'Menarik! Anda ingin menghubungkan React dengan AI? Saya bisa jelaskan tentang React AI integrations, React dengan TensorFlow.js, atau bagaimana AI enhancing React applications.';
      }
    }
    
    // Default intelligent response
    const defaultResponses = [
      `Pertanyaan menarik! Meskipun saya belum memiliki informasi spesifik tentang "${msg}", saya bisa membantu dengan topik teknologi, informasi tentang Mahin Utsman Nawawi, atau topik lainnya. Ketik "help" untuk melihat kemampuan saya!`,
      `Maaf, saya belum sepenuhnya memahami pertanyaan Anda. Namun, saya bisa membantu dengan berbagai topik seperti React, AI, No-Code, Web3, dan informasi tentang projek portfolio. Ada yang bisa saya bantu?`,
      `Hmm, saya masih belajar untuk memahami pertanyaan seperti ini. Tapi saya ahli dalam topik teknologi dan informasi tentang Mahin Utsman Nawawi. Coba tanyakan hal lain atau ketik "help"!`,
    ];
    
    return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
  };

  const sendMessage = () => {
    if (!input.trim()) return;

    const userInput = input;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: userInput,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setTyping(true);

    // Enhanced conversation context tracking
    const msg = userInput.toLowerCase();
    setConversationContext((prev) => {
      const newContext = { ...prev };
      
      // Track previous questions for context
      newContext.previousQuestions = [...prev.previousQuestions, userInput].slice(-10);
      
      // Enhanced topic tracking with follow-up detection
      let detectedTopic = null;
      
      // Direct topic keywords
      if (msg.includes('react') || msg.includes('ai') || msg.includes('web3') || msg.includes('blockchain') || msg.includes('nocode') || msg.includes('teknologi') || msg.includes('tech')) {
        detectedTopic = 'technology';
      } else if (msg.includes('mahin') || msg.includes('projek') || msg.includes('skill') || msg.includes('kontak')) {
        detectedTopic = 'about';
      } 
      // Follow-up question detection - maintain previous topic if asking for clarification
      else if (prev.lastTopic && (
        msg.includes('jelaskan') || msg.includes('detail') || msg.includes('lagi') || msg.includes('selain') || 
        msg.includes('apa itu') || msg.includes('bagaimana') || msg.includes('contoh') || 
        msg.includes('more') || msg.includes('explain') || msg.includes('lebih') ||
        msg.includes('?')
      )) {
        detectedTopic = prev.lastTopic; // Maintain previous topic for follow-ups
      }
      
      if (detectedTopic) {
        newContext.lastTopic = detectedTopic;
        newContext.topics = [...new Set([...prev.topics, detectedTopic])];
        newContext.followUpCount = prev.lastTopic === detectedTopic ? prev.followUpCount + 1 : 1;
      } else {
        newContext.followUpCount = prev.followUpCount + 1;
      }
      
      return newContext;
    });

    // Simulate realistic AI processing time
    const processingTime = 800 + Math.random() * 700; // 800-1500ms
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getIntelligentResponse(userInput, conversationContext),
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botResponse]);
      setTyping(false);
    }, processingTime);
  };

  return (
    <div className="min-h-screen bg-dark p-4">
      <div className="container mx-auto max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="glass-card p-4 mb-4 flex items-center gap-4">
          <a href="/#projects" className="text-neon-blue hover:text-neon-green transition">
            <ArrowLeft size={24} />
          </a>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-neon-blue/20 flex items-center justify-center">
              <Bot className="text-neon-blue" size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">AI Chatbot Assistant</h1>
              <p className="text-xs text-neon-green">Online • Intelligent NLP</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 glass-card p-6 overflow-y-auto mb-4 space-y-4">
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`flex gap-3 max-w-[70%] ${
                    message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.sender === 'user'
                        ? 'bg-neon-green/20'
                        : 'bg-neon-blue/20'
                    }`}
                  >
                    {message.sender === 'user' ? (
                      <User size={16} className="text-neon-green" />
                    ) : (
                      <Bot size={16} className="text-neon-blue" />
                    )}
                  </div>
                  <div>
                    <div
                      className={`p-4 rounded-2xl ${
                        message.sender === 'user'
                          ? 'bg-neon-green/10 border border-neon-green/30'
                          : 'bg-neon-blue/10 border border-neon-blue/30'
                      }`}
                    >
                      <p className="text-white text-sm whitespace-pre-line">{message.text}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {message.timestamp.toLocaleTimeString('id-ID', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing Indicator */}
          {typing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-neon-blue/20 flex items-center justify-center">
                <Bot size={16} className="text-neon-blue" />
              </div>
              <div className="bg-neon-blue/10 border border-neon-blue/30 p-4 rounded-2xl">
                <div className="flex gap-1">
                  <motion.div
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 0.6, repeat: Infinity }}
                    className="w-2 h-2 bg-neon-blue rounded-full"
                  />
                  <motion.div
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                    className="w-2 h-2 bg-neon-blue rounded-full"
                  />
                  <motion.div
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                    className="w-2 h-2 bg-neon-blue rounded-full"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Input */}
        <div className="glass-card p-4">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Ketik pesan Anda..."
              className="flex-1 px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue transition"
            />
            <button onClick={sendMessage} className="btn-neon flex items-center gap-2">
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
